import React from "react";

import { ErrorSpan } from "@/components/ErrorSpan";
import { LineItemUOMQuantity } from "@/features/job-tracking/components/LineItemUOMQuantity";
import { SOSEntityLineItemHashLink } from "@/components/SosEntityLineItemHashLink";
import { formatDineroCad } from "@/utils/dinero-util";
import { CellContextComponent } from "@/utils/react-table-utils";
import { CostTaxBreakdown } from "../../queries/tracking-data/cost-tax-breakdown";
import {
  ItemReceipt,
  ItemReceiptLineItem,
  LinkedPoDetails,
  ReceivingCost,
} from "../../queries/tracking-data/item-receipts";
import { ItemSelectResult } from "../../queries/tracking-data/items";
import { BinCell } from "../BinCell";
import { LineItemDescription } from "../LineItemDescription";
import { JobTrackingInternalLink } from "../JobTrackingInternalLink";

export type LandedCostsTableRow = LineItemTableRow | ReceivingCostTableRow;

export interface TableRowBase {
  rowId: string;
  receipt: ItemReceipt;
  normalizedLine: NormalizedLineItem;
}

export interface LineItemTableRow extends TableRowBase {
  lineItem: ItemReceiptLineItem;
}

export interface ReceivingCostTableRow extends TableRowBase {
  receivingCost: ReceivingCost;
}

/**
 * This represents either an ItemReceiptLineItem, or a ReceivingCost line item normalized to include the data for the table.
 *
 * Note that for several columns we define both a primitive value and a component to render the value. This allows us to
 * maintain sorting capabilities for the column with the primitive value, while also allowing us to render a separate
 * component for either `ItemReceiptLineItem` or `ReceivingCost` lines.
 */
export interface NormalizedLineItem {
  id: number;
  lineNumber: number | string;
  item: ItemSelectResult;
  description: string | null;
  descriptionElement: React.ReactNode;
  uomQuantity: number;
  uomQuantityElement: React.ReactNode;
  unitCost: string;
  unitCostElement: React.ReactNode;
  totalCost: CostTaxBreakdown;
  bin: ItemReceiptLineItem["bin"];
  BinComponent: CellContextComponent<LandedCostsTableRow>;
  linkedPo: LinkedPoDetails | undefined;
  linkedPoComponent: CellContextComponent<LandedCostsTableRow>;
}

export function normalizeLineItem(lineItem: ItemReceiptLineItem): NormalizedLineItem {
  const { id, lineNumber, item, unitCost: unitCostDinero, totalCost, bin, linkedPo } = lineItem;
  const unitCost = formatDineroCad(unitCostDinero.subtotal);
  return {
    id,
    lineNumber,
    item,
    description: lineItem.description,
    descriptionElement: <LineItemDescription line={lineItem} />,
    uomQuantity: lineItem.uomQuantity,
    uomQuantityElement: <LineItemUOMQuantity line={lineItem} />,
    unitCost,
    unitCostElement: unitCost,
    totalCost,
    bin,
    BinComponent: BinCellComponent,
    linkedPo,
    linkedPoComponent: LinkedPoCellComponent,
  };
}

const BinCellComponent: CellContextComponent<LandedCostsTableRow> = ({ row }) => (
  <BinCell bin={row.original.normalizedLine.bin} warnForNonSORack={true} />
);

const LinkedPoCellComponent: CellContextComponent<LandedCostsTableRow> = ({ row }) => {
  const linkedPo = row.original.normalizedLine.linkedPo;
  if (!linkedPo) return null;

  return (
    <JobTrackingInternalLink trackerPath="purchase-orders">
      {({ targetPath, targetPageTitle }) => (
        <SOSEntityLineItemHashLink
          fromHash={row.id}
          urlPath={targetPath}
          tooltipContent={`View on '${targetPageTitle}' page`}
          type="purchaseorder"
          id={linkedPo.id}
          lineNumber={0}
          label={linkedPo.number}
        />
      )}
    </JobTrackingInternalLink>
  );
};

export function normalizeReceivingCost(receivingCost: ReceivingCost, receipt: ItemReceipt): NormalizedLineItem {
  const { id, lineNumber, item, vendor, jobAmountCad } = receivingCost;
  const jobPercent = receipt.receivingCostsJobPercent;
  const vendorLabel = vendor ? `(${vendor.name})` : <ErrorSpan>⚠ Vendor Missing</ErrorSpan>;
  const descriptionElement = (
    <>
      Other Costs ({(jobPercent * 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}% allocated to this job):{" "}
      {item.description} {vendorLabel}
    </>
  );
  return {
    id,
    lineNumber: receipt.allLines.length + lineNumber,
    item,
    description: item.description,
    descriptionElement,
    uomQuantity: NaN,
    uomQuantityElement: naElement,
    unitCost: "N/A",
    unitCostElement: naElement,
    totalCost: jobAmountCad,
    bin: { id: -1, number: "N/A" },
    BinComponent: NAComponent,
    linkedPo: undefined,
    linkedPoComponent: NAComponent,
  };
}

const naElement = <span className="text-secondary">N/A</span>;
const NAComponent: React.FC = () => naElement;
